import { useState, useEffect, useRef } from 'react';
import { Loader2, Check, Calendar } from 'lucide-react';
import axios from 'axios';
import { trackClick } from '@/lib/track';
import { trackRsvp } from '@/lib/fanProfile';
import { TONIGHT_EVENT } from '@/config/tonightEvent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getCountdownText(scheduledAt) {
  const now = new Date();
  const event = new Date(scheduledAt);
  const diffMs = event - now;
  if (diffMs <= 0) {
    // During event: within 60 min after start
    if (diffMs > -3600000) return 'Live now';
    return 'Ended';
  }
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  return `In ${diffDays} days`;
}

function generateICS() {
  const e = TONIGHT_EVENT;
  const start = new Date(e.scheduled_at).toISOString().replace(/[-:]/g, '').replace('.000', '');
  const end = new Date(new Date(e.scheduled_at).getTime() + 60 * 60000).toISOString().replace(/[-:]/g, '').replace('.000', '');
  const ics = `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nDTSTART:${start}\nDTEND:${end}\nSUMMARY:${e.title}\nDESCRIPTION:${e.description}\nEND:VEVENT\nEND:VCALENDAR`;
  const blob = new Blob([ics], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'fan-meet.ics'; a.click();
  URL.revokeObjectURL(url);
}

export default function TonightSection() {
  const e = TONIGHT_EVENT;
  const [rsvpCount, setRsvpCount] = useState(0);
  const [countdown, setCountdown] = useState(getCountdownText(e.scheduled_at));
  const [showRsvp, setShowRsvp] = useState(false);
  const [rsvpDone, setRsvpDone] = useState(false);
  const [rsvpForm, setRsvpForm] = useState({ name: '', email: '' });
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  // Live fan counter — reads from /api/mail/stats every 30s
  useEffect(() => {
    const fetchCount = () => {
      axios.get(`${API}/mail/stats?adminKey=at-admin-2026`).then(res => {
        setRsvpCount(res.data.totalSubscribers || res.data.mongoSubscribers || 0);
      }).catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Live countdown — update every 60s
  useEffect(() => {
    const update = () => setCountdown(getCountdownText(e.scheduled_at));
    update(); // immediate
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [e.scheduled_at]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) entry.target.querySelectorAll('.reveal-el').forEach(c => c.classList.add('visible')); }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handleRsvp = async (ev) => {
    ev.preventDefault();
    if (!rsvpForm.name || !rsvpForm.email) return;
    setLoading(true);
    try {
      trackRsvp();
      trackClick({ film: e.film, platform: 'watch_together', action: 'rsvp', section: 'tonight' });
      const rsvpResult = await axios.post(`${API}/mail/event-rsvp`, {
        name: rsvpForm.name,
        email: rsvpForm.email,
        eventName: e.title,
        eventDate: 'Wednesday, June 24, 2026 · 9:00 PM IST',
      });
      if (rsvpResult.data.ok) {
        setRsvpDone(true);
        setRsvpCount(c => c + 1);
      } else {
        alert('Something went wrong — please try again or email AT@avinashtiwary.com directly.');
      }
    } catch (err) {
      console.error('RSVP error:', err);
      alert('Something went wrong — please try again or email AT@avinashtiwary.com directly.');
    }
    finally { setLoading(false); }
  };

  const dateStr = new Date(e.scheduled_at).toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <section id="tonight" data-testid="tonight-section" ref={ref} className="py-24 md:py-40 px-5 md:px-10 lg:px-20 bg-tonight">
      <div className="reveal-el relative overflow-hidden rounded-sm" style={{ border: '1px solid rgba(201,168,76,0.08)' }}>
        <div className="absolute inset-0">
          <div className="w-full h-full" style={{ background: 'linear-gradient(135deg, #0a0e1a 0%, #1a1510 50%, #0a0e1a 100%)' }} />
          <div className="absolute inset-0" style={{ background: 'rgba(10,10,10,0.5)' }} />
        </div>

        <div className="relative z-10 p-8 md:p-12 lg:p-16">
          {/* Badge + countdown */}
          <div className="flex items-center gap-4 mb-3">
            <span className="font-['Inter'] text-[9px] tracking-[0.3em] uppercase px-4 py-1.5 rounded-full" style={{ color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.3)', background: 'rgba(201,168,76,0.05)' }}>Tonight</span>
            <span className="font-['Inter'] text-[11px] font-medium" style={{ color: 'rgba(201,168,76,0.6)' }}>{countdown}</span>
          </div>

          <p className="font-['Inter'] text-xs mb-6" style={{ color: 'var(--ash)' }}>{e.eyebrow}</p>

          <h3 className="font-['Playfair_Display'] italic text-3xl md:text-4xl lg:text-5xl font-normal leading-tight" style={{ color: 'var(--warm-cream)' }}>
            {e.title}
          </h3>

          <p className="font-['Space_Mono'] text-xs mt-5" style={{ color: 'var(--ash)' }}>
            {dateStr} &bull; 9:00 PM IST
          </p>

          <p className="font-['Cormorant_Garamond'] italic text-base md:text-lg mt-4 max-w-xl leading-relaxed" style={{ color: 'var(--ash)' }}>
            {e.description}
          </p>

          {/* Live fan counter */}
          <div className="flex items-center gap-2 mt-5">
            <div className="flex -space-x-1.5">
              {Array.from({ length: Math.min(5, rsvpCount) }).map((_, i) => (
                <div key={i} className="w-5 h-5 rounded-full text-[7px] font-['Inter'] font-bold flex items-center justify-center" style={{ background: `hsl(${35 + i * 25}, 55%, ${40 + i * 4}%)`, color: 'var(--warm-cream)', border: '2px solid var(--deep-black)' }}>{String.fromCharCode(65 + i)}</div>
              ))}
            </div>
            <p className="font-['Inter'] text-xs" style={{ color: 'rgba(201,168,76,0.55)' }}>
              <span className="font-semibold">{rsvpCount}</span> fans have already RSVP'd
            </p>
          </div>

          {/* CTAs */}
          <div className="mt-10">
            {rsvpDone ? (
              <div data-testid="rsvp-success">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ border: '1px solid var(--gold)' }}><Check className="w-4 h-4" style={{ color: 'var(--gold)' }} /></div>
                  <div>
                    <p className="font-['Playfair_Display'] italic text-lg" style={{ color: 'var(--warm-cream)' }}>{e.postRsvp.heading}</p>
                    <p className="font-['Inter'] text-[11px] mt-1" style={{ color: 'var(--ash)' }}>{e.postRsvp.subhead}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 mt-6">
                  <button onClick={generateICS} className="flex items-center gap-2 px-4 py-2 rounded-full text-[11px] font-['Inter'] transition-all hover:bg-white/5" style={{ border: '1px solid rgba(201,168,76,0.2)', color: 'var(--gold)' }}>
                    <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />Add to Calendar
                  </button>
                  {e.joinRoomUrl && (
                    <a href={e.joinRoomUrl} className="flex items-center gap-2 px-4 py-2 rounded-full text-[11px] font-['Inter'] transition-all hover:bg-white/5" style={{ border: '1px solid rgba(201,168,76,0.2)', color: 'var(--gold)' }}>
                      Join the Room
                    </a>
                  )}
                </div>
              </div>
            ) : showRsvp ? (
              <form onSubmit={handleRsvp} className="max-w-sm space-y-5">
                <input data-testid="rsvp-name" type="text" value={rsvpForm.name} onChange={ev => setRsvpForm(f => ({ ...f, name: ev.target.value }))} placeholder="Name" className="w-full bg-transparent border-0 border-b px-0 py-3 text-sm font-['Inter'] focus:ring-0 focus:outline-none" style={{ borderColor: 'rgba(201,168,76,0.15)', color: 'var(--warm-cream)' }} />
                <input data-testid="rsvp-email" type="email" value={rsvpForm.email} onChange={ev => setRsvpForm(f => ({ ...f, email: ev.target.value }))} placeholder="Email" className="w-full bg-transparent border-0 border-b px-0 py-3 text-sm font-['Inter'] focus:ring-0 focus:outline-none" style={{ borderColor: 'rgba(201,168,76,0.15)', color: 'var(--warm-cream)' }} />
                <button data-testid="rsvp-submit" type="submit" disabled={loading} className="font-['Inter'] text-xs tracking-[0.15em] uppercase px-6 py-2.5 rounded-full transition-all disabled:opacity-50" style={{ background: 'var(--gold)', color: 'var(--deep-black)' }}>{loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "I'll be there"}</button>
              </form>
            ) : (
              <div className="flex flex-wrap gap-4">
                <button data-testid="tonight-rsvp-btn" onClick={() => setShowRsvp(true)} className="font-['Inter'] text-sm font-semibold tracking-[0.15em] uppercase px-10 py-4 rounded-full transition-all hover:shadow-[0_0_20px_rgba(201,168,76,0.15)]" style={{ color: 'var(--gold)', border: '2px solid rgba(201,168,76,0.35)' }}>
                  Save Your Place
                </button>
                {e.joinRoomUrl && (
                  <a href={e.joinRoomUrl} className="font-['Inter'] text-sm tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-all hover:bg-white/5" style={{ color: 'var(--ash)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    Join the Room
                  </a>
                )}
              </div>
            )}
          </div>

          {/* WhatsApp Community */}
          <div className="mt-8">
            <p className="font-['Inter'] text-[11px] mb-3" style={{ color: 'var(--ash)', opacity: 0.5 }}>Or if you'd rather not email:</p>
            <a
              href="https://chat.whatsapp.com/DmChZFC7pPS0HiOh4QG03b"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Join the Avinash Tiwary WhatsApp community"
              onClick={() => trackClick({ film: 'community', platform: 'whatsapp_community', action: 'join', section: 'tonight' })}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[12px] font-['Inter'] tracking-wide transition-all hover:bg-[rgba(37,211,102,0.08)]"
              style={{ border: '1px solid rgba(37,211,102,0.25)', color: '#25D366' }}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/></svg>
              Join the WhatsApp community
            </a>
            <p className="font-['Inter'] text-[10px] mt-2" style={{ color: 'var(--ash)', opacity: 0.35 }}>Get the Meet link + updates direct on WhatsApp. One tap. No DM needed.</p>
          </div>

          {/* Urgency */}
          <p className="font-['Cormorant_Garamond'] italic text-xs mt-8" style={{ color: 'var(--ash)', opacity: 0.5 }}>
            {e.urgency}
          </p>
        </div>
      </div>
    </section>
  );
}
